/**
 * NotFoundPage — generic 404 / "resource doesn't exist" page.
 *
 * Used by:
 *   - PipelineDetailPage when workflow row is missing
 *   - App.js catch-all <Route path="*"> (any unknown URL)
 *   - Any other "resource ID in URL doesn't resolve" surface
 *
 * Props are all optional so it works out of the box; pass overrides
 * for context-specific copy + actions.
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { cubeOutline, arrowBackOutline } from 'ionicons/icons';
import './NotFoundPage.css';

export default function NotFoundPage({
  code = '404',
  title = 'Page not found',
  subtitle = "The link you followed may be broken, or the resource may have been moved or deleted.",
  primaryAction,   // { label, to } — overrides default "Go back"
  secondaryAction, // { label, to } — overrides default "Marketplace"
}) {
  const navigate = useNavigate();

  const primary = primaryAction || { label: 'Go back', onClick: () => navigate(-1), icon: arrowBackOutline };
  const secondary = secondaryAction || { label: 'Browse marketplace', to: '/marketplace', icon: cubeOutline };

  return (
    <div className="notfound-page">
      <div className="notfound-card">
        <div className="notfound-code" aria-hidden="true">{code}</div>
        <h1 className="notfound-title">{title}</h1>
        <p className="notfound-sub">{subtitle}</p>

        <div className="notfound-actions">
          {primary && (
            primary.to ? (
              <Link to={primary.to} className="notfound-btn notfound-btn-primary">
                {primary.icon && <IonIcon icon={primary.icon} />}
                <span>{primary.label}</span>
              </Link>
            ) : (
              <button
                type="button"
                className="notfound-btn notfound-btn-primary"
                onClick={primary.onClick}
              >
                {primary.icon && <IonIcon icon={primary.icon} />}
                <span>{primary.label}</span>
              </button>
            )
          )}
          {secondary && (
            <Link to={secondary.to} className="notfound-btn notfound-btn-secondary">
              {secondary.icon && <IonIcon icon={secondary.icon} />}
              <span>{secondary.label}</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
